import { BulkUpdateAttributeDefinition } from '@provision/bulk-update-config';
import { ConversationStatus } from '@provision/conversation-types';
import { Conversation, EntityBase } from '@provision/entity-types';
import { arrayIfy } from '@provision/formatting-utils';
import { useMemo } from 'react';
import { UserGroupRules } from 'src/graphql';
import {
  BulkOption,
  getBulkOptionKey,
} from 'src/pages/CustomViews/components/bulk-actions-modal.utils';
import { hasBulkUpdateActionAccess } from 'src/pages/CustomViews/utils';

interface BulkActionOptionConfig {
  editorType?: string;
  options?: BulkOption[];
  optionsQuery?: string;
}

interface QueryOptionValue {
  options?: BulkOption[];
  historicalOptions?: BulkOption[];
  isLoading?: boolean;
  onInputChange?: (value: string) => void;
}

interface UseBulkActionOptionsParams {
  entityName: string;
  records: EntityBase[];
  selectedBulkAction?: BulkActionOptionConfig;
  selectedAttrConfig?: BulkUpdateAttributeDefinition;
  queryOptions: Record<string, QueryOptionValue>;
  groupRules: Record<string, boolean | number[] | undefined>;
  isServiceLevelBulkUpdate: boolean;
  constrainedServiceLevelOptions: BulkOption[];
  partnerId: number | null;
}

interface UseBulkActionOptionsResult {
  selectedBulkOptions: BulkOption[];
  selectedBulkQueryValue: QueryOptionValue | undefined;
  selectedBulkAutocompleteOptions: BulkOption[];
}

export function useBulkActionOptions({
  entityName,
  records,
  selectedBulkAction,
  selectedAttrConfig,
  queryOptions,
  groupRules,
  isServiceLevelBulkUpdate,
  constrainedServiceLevelOptions,
  partnerId,
}: UseBulkActionOptionsParams): UseBulkActionOptionsResult {
  const assigneeGroupIds = useMemo(() => {
    if (entityName !== 'Conversation') {
      return [];
    }

    const assigneeGroupIds = records
      .map((record) => (record as Conversation).conversationAssigneeGroupId || '')
      .filter(Boolean);

    return [...new Set(assigneeGroupIds)];
  }, [entityName, records]);

  const selectedBulkOptions = useMemo<BulkOption[]>(() => {
    if (selectedBulkAction?.options?.length) {
      return selectedBulkAction.options;
    }

    const queryKey = selectedBulkAction?.optionsQuery;
    const options = queryKey ? (queryOptions[queryKey]?.options ?? []) : [];
    const accessRuleKeys = arrayIfy(selectedAttrConfig?.validation?.accessRuleKey);

    if (isServiceLevelBulkUpdate) {
      return constrainedServiceLevelOptions;
    }

    if (selectedAttrConfig?.editor.optionsSource === 'getPlatformUser') {
      return options.filter((option: BulkOption) => {
        const hasUpdateConversationRule = option.userGroups?.some((group) =>
          group.ruleValues?.some(
            (rule: { userGroupRuleId?: string | null }) =>
              rule.userGroupRuleId === UserGroupRules.AccessToUpdateConversation,
          ),
        );

        if (hasUpdateConversationRule === false) {
          return false;
        }

        if (!assigneeGroupIds.length) {
          return true;
        }

        if (!option.conversationAssigneeGroups?.length) {
          return true;
        }

        return option.conversationAssigneeGroups?.some((group: { id?: string | null }) => {
          const groupId = String(group?.id || '');
          return assigneeGroupIds.includes(groupId);
        });
      });
    }

    if (accessRuleKeys.length === 0) {
      return options;
    }

    if (
      selectedAttrConfig &&
      hasBulkUpdateActionAccess(selectedAttrConfig.validation, groupRules)
    ) {
      return options;
    }

    return options.filter(
      (option) => String(option.id).toLowerCase() !== ConversationStatus.Closed.toLowerCase(),
    );
  }, [
    constrainedServiceLevelOptions,
    groupRules,
    isServiceLevelBulkUpdate,
    queryOptions,
    selectedAttrConfig,
    selectedBulkAction,
    assigneeGroupIds,
  ]);

  const selectedBulkQueryValue = useMemo(() => {
    const queryKey = selectedBulkAction?.optionsQuery;
    if (!queryKey) {
      return undefined;
    }
    return queryOptions[queryKey];
  }, [queryOptions, selectedBulkAction]);

  const selectedBulkAutocompleteOptions = useMemo(() => {
    const merged = [
      ...(selectedBulkQueryValue?.historicalOptions ?? []),
      ...selectedBulkOptions,
    ] as BulkOption[];

    const byKey = new Map<string, BulkOption>();

    for (const option of merged) {
      byKey.set(getBulkOptionKey(option), option);
    }
    const allOptions = [...byKey.values()];

    const filtersOptionsByPartner =
      selectedAttrConfig?.editor.optionsSource === 'getConversationParticipantOptions';

    if (!filtersOptionsByPartner || partnerId === null) {
      return allOptions;
    }

    return allOptions.filter((option) => {
      if (option.entityPartnerId === null || option.entityPartnerId === undefined) {
        return true;
      }
      return Number(option.entityPartnerId) === partnerId;
    });
  }, [
    partnerId,
    selectedAttrConfig,
    selectedBulkOptions,
    selectedBulkQueryValue?.historicalOptions,
  ]);

  return {
    selectedBulkOptions,
    selectedBulkQueryValue,
    selectedBulkAutocompleteOptions,
  };
}
